import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  OPPORTUNITY_BUCKETS,
  OPPORTUNITY_BUCKET_LABELS,
  CANONICAL_TO_OPPORTUNITY_BUCKET,
  MARKET_NAMES,
  MARKETS,
  screenerFilterForBucket,
  type OpportunityBucket,
  type MarketCode,
} from '@hc/shared';
import { BookOpen, Compass } from 'lucide-react';
import { PanelFrame } from '../panel-frame.js';
import { PanelEmptyState } from '../panel-empty-state.js';
import { useOpenMethodology } from '../use-open-methodology.js';
import { ramp, textOn, NA_HATCH, NA_HATCH_DARK } from '../heat-ramp.js';
import { ReimbursementBody } from './reimbursement.js';
import {
  useActiveCountry,
  useGlobalCurrency,
  useRegionLevel,
  useSetRegionLevel,
  useAppStore,
  type PanelId,
  type TabId,
} from '../store.js';
import { useDockviewApi } from '../dockview-context.js';
import { useMandateCountries } from '../use-mandate-defaults.js';
import type { PanelParams, PanelComponentKey } from '../components.js';
import { useMarketOpportunity, useOrigination } from '../../api/hooks.js';
import type { MarketOpportunityCell } from '@hc/shared';
import type { OriginationCell, OriginationComponent } from '../../api/client.js';
import { convertFromEur, CURRENCY_SYMBOL, type GlobalCurrency } from '../../currency.js';

// ---- Corryk Opportunity Index (composite, region × subsegment) -------------
// Six confidence-weighted factors blended client-side with FIXED weights
// (DEFAULT_WEIGHTS) — our proprietary index, not user-tunable; missing factors
// (momentum/workforce in v1) drop out. The matrix is bucket-keyed, so
// canonical-subsegment cells fold up to their bucket.
const ORIG_FACTORS = [
  { key: 'fragmentation', label: 'Fragmentation', w: 0.25 },
  { key: 'whitespace', label: 'White space', w: 0.2 },
  { key: 'succession', label: 'Succession', w: 0.2 },
  { key: 'acquisition', label: 'Acquisition', w: 0.15 },
  { key: 'momentum', label: 'Momentum', w: 0.1 },
  // Demand landed 2026-07-11 (official population projections, 2025→2035
  // growth banded by subsegment). Workforce stays unfed and drops out.
  { key: 'demand', label: 'Demand', w: 0.15 },
  { key: 'workforce', label: 'Workforce', w: 0.1 },
] as const;
type FactorKey = (typeof ORIG_FACTORS)[number]['key'];
const DEFAULT_WEIGHTS = Object.fromEntries(ORIG_FACTORS.map((f) => [f.key, f.w])) as Record<
  FactorKey,
  number
>;
// Opportunity Index cells arrive keyed on the canonical sub-segment, so they map
// onto PARENT rows only — the Index is computed at sub-segment grain, never at
// specialty grain. Specialty child rows therefore show "—" in the Origination
// view rather than inheriting their parent's score, which would invent
// differentiation the model never produced. (The matrix's other views are
// per-bucket and do populate the child rows.)
const CANONICAL_TO_BUCKET: Record<string, OpportunityBucket> = CANONICAL_TO_OPPORTUNITY_BUCKET;

/** The value the Screener's mixed sub-segment filter expects for a bucket: the
 *  canonical display label for a parent, the specialty token for a fine row.
 *  (buildScreenerQuery splits that one set back into subsegment[] + specialty[].) */
function subsegmentChipForBucket(bucket: OpportunityBucket): string[] {
  const f = screenerFilterForBucket(bucket);
  const v = f.subsegment?.[0] ?? f.specialty?.[0];
  return v ? [v] : [];
}
type FactorComp = Record<FactorKey, OriginationComponent>;
function blendScore(comp: FactorComp, w: Record<FactorKey, number>): number | null {
  let num = 0;
  let den = 0;
  for (const f of ORIG_FACTORS) {
    const x = comp[f.key];
    if (x && x.f != null && x.c > 0) {
      num += w[f.key] * x.c * x.f;
      den += w[f.key] * x.c;
    }
  }
  return den > 0 ? Math.round(100 * (num / den)) : null;
}

// Market / Opportunity — region heatmap (altitude B). A bucket × region
// consolidation-opportunity matrix for one market, fed by /market/opportunity.
// Aggregate panel (no entity binding); reads the active country + the global
// display currency from the workspace store. The opportunity index is EUR-
// anchored, so only the € cell values rebase with the currency.

type ViewKey = 'origination' | 'market' | 'cr5' | 'reimbursement';
// Altitude A = country rollup (subsegment × country, cross-market entry view);
// B = region heatmap (subsegment × region, one market). The brief's two
// altitudes live in one panel, toggled — the drill chain runs A → B → Screener.
type Altitude = 'region' | 'country';

// The panel's market roster — the region-heatmap picker, the A→B drill, and the
// country-rollup columns. Was ['GB','FR'] for the region altitude and the
// 11-entry TARGET_MARKETS list for the country altitude; both are retired.
//
// Un-gated to all 27 on 2026-07-20, once every market materialised region cells
// at all three NUTS levels AND the market_cell_aggregates that feed the
// Opportunity Index overlay (verified: matching region counts in both tables for
// every market). Thin markets stay honest rather than hidden — EE and LV resolve
// a single NUTS2 region, and the NUTS level selector is how a user goes finer.
//
// Kept as a named constant so a market whose regional data regresses can be
// pulled out here, in one place. Sorted by display name — MARKETS is in registry-
// onboarding order, which was fine as a 2-item gate and is unscannable as a
// 27-item dropdown and column axis.
const REGION_MARKETS: readonly MarketCode[] = [...MARKETS].sort((a, b) =>
  MARKET_NAMES[a].localeCompare(MARKET_NAMES[b]),
);

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));

// cream → accent (the opportunity heat) — shared with Reimbursement, see
// workspace/heat-ramp.ts.

// null CR5 means the cell's top five operators are all tied on one modelled
// constant, so there is no concentration to describe — see the API route.
// "Not measurable" is the honest label; "Highly fragmented" would be a claim.
function fragLabel(cr5: number | null): string {
  if (cr5 == null) return 'Not measurable';
  if (cr5 < 0.3) return 'Highly fragmented';
  if (cr5 < 0.5) return 'Fragmented';
  if (cr5 < 0.7) return 'Moderately concentrated';
  return 'Concentrated';
}
// HHI on the standard 0–10000 points scale (Σ shareᵢ²·10000), DOJ/FTC bands.
function hhiLabel(hhi: number | null): string {
  if (hhi == null) return 'not measurable';
  const pts = hhi * 10000;
  if (pts < 1500) return 'unconcentrated';
  if (pts < 2500) return 'moderately concentrated';
  return 'highly concentrated';
}

function fmtMoney(amountEur: number, ccy: GlobalCurrency, decimals = 1): string {
  const v = convertFromEur(amountEur, ccy);
  const sym = CURRENCY_SYMBOL[ccy];
  const a = Math.abs(v);
  let body: string;
  if (a >= 1e12) body = (v / 1e12).toFixed(decimals) + 'T';
  else if (a >= 1e9) body = (v / 1e9).toFixed(decimals) + 'B';
  else if (a >= 1e6) body = (v / 1e6).toFixed(decimals) + 'M';
  else if (a >= 1e3) body = (v / 1e3).toFixed(decimals) + 'K';
  else body = String(Math.round(v));
  return sym + body;
}
const fmtN = (v: number) => v.toLocaleString('en-GB');

const VIEWS: Record<
  ViewKey,
  {
    label: string;
    t: (c: MarketOpportunityCell, ranges: Ranges) => number;
    val: (c: MarketOpportunityCell, ccy: GlobalCurrency) => string;
    sub: (c: MarketOpportunityCell) => string;
    scale: { lo: string; hi: string; cap: string };
  }
> = {
  // The Corryk Opportunity Index — the region × subsector composite (six
  // confidence-weighted factors), the panel's headline lens. Heat is computed
  // from the separate /origination data, not the MarketOpportunityCell, so
  // t/val here are placeholders (the cell render branches on
  // view==='origination'); only label + scale are used. Region-altitude only.
  origination: {
    label: 'Corryk Opportunity Index',
    t: () => 0,
    val: () => '',
    sub: () => '',
    scale: {
      lo: '0',
      hi: '100',
      cap: 'Corryk Opportunity Index — weighted: fragmentation + white space + succession + acquisition',
    },
  },
  market: {
    label: 'Market Size',
    t: (c, r) => logT(c.marketRevenueEur, r.market),
    val: (c, ccy) => fmtMoney(c.marketRevenueEur, ccy),
    sub: (c) => fmtN(c.nTotal) + ' cos',
    scale: { lo: 'small', hi: 'large', cap: 'Σ revenue, all operators' },
  },
  cr5: {
    label: 'Market Fragmentation',
    // Fragmentation = 1 − CR5. High fragmentation reads hot, aligning the
    // colour with the buy-and-build thesis (fragmented = more to consolidate).
    // null CR5 (top five tied) follows the same convention as a missing
    // origination score: coldest cell, em-dash value, ◔ caution marker.
    t: (c) => (c.cr5 == null ? 0 : 1 - c.cr5),
    val: (c) => (c.cr5 == null ? '—' : Math.round((1 - c.cr5) * 100) + '%'),
    sub: () => '',
    scale: {
      lo: 'consolidated',
      hi: 'fragmented',
      cap: 'fragmentation = 1 − CR5 (top-5 operators’ revenue share)',
    },
  },
  // Reimbursement lens — COUNTRY-altitude only. The body branches to
  // <ReimbursementBody/>, so t/val/sub here are inert placeholders; only the
  // label is used (in the view toggle).
  reimbursement: {
    label: 'Reimbursement',
    t: () => 0,
    val: () => '',
    sub: () => '',
    scale: { lo: '', hi: '', cap: '' },
  },
};

interface Ranges {
  market: { lo: number; hi: number };
}
function logT(v: number, r: { lo: number; hi: number }) {
  if (v <= 0 || r.hi <= r.lo) return 0;
  return clamp((Math.log10(v) - r.lo) / (r.hi - r.lo), 0, 1);
}

export function MarketOpportunityPanel({ tabId, panelId }: { tabId: TabId; panelId: PanelId }) {
  const activeCountry = useActiveCountry();
  const regionLevel = useRegionLevel();
  const setRegionLevel = useSetRegionLevel();
  const ccy = useGlobalCurrency();
  // Per-panel market picker — seeding priority: the workspace active-country
  // filter (an explicit user choice) → the buyer's mandated market → a random
  // market. Never a hardcoded UK default: with 27 served markets, always
  // opening on GB reads as "this is a UK product", which it is not. Random (not
  // alphabetical-first) keeps the fallback honest — Austria-every-time would
  // just be a different hardcode. Independent of the workspace filter after open.
  const workspaceSeeded = REGION_MARKETS.includes(activeCountry as MarketCode);
  const [country, setCountry] = useState<MarketCode>(() =>
    workspaceSeeded ? (activeCountry as MarketCode) : 'GB',
  );
  const countrySeeded = useRef(workspaceSeeded);
  const { countries: mandateCountries, loaded: mandatesLoaded } = useMandateCountries();
  useEffect(() => {
    if (countrySeeded.current || !mandatesLoaded) return;
    countrySeeded.current = true;
    const mandated = mandateCountries.find((c) => REGION_MARKETS.includes(c));
    setCountry(
      mandated ?? REGION_MARKETS[Math.floor(Math.random() * REGION_MARKETS.length)] ?? 'GB',
    );
  }, [mandatesLoaded, mandateCountries]);
  // Any explicit pick (market dropdown, A→B drill) ends the seeding window.
  const pickCountry = (mkt: MarketCode) => {
    countrySeeded.current = true;
    setCountry(mkt);
  };
  const [view, setView] = useState<ViewKey>('origination');
  const [altitude, setAltitude] = useState<Altitude>('region');
  // Row set is view-dependent. The Corryk Opportunity Index is modelled at
  // sub-segment grain, so its fine specialty rows could only ever be blank —
  // showing ten dead rows would read as a broken panel, not as honesty. Every
  // other view aggregates per bucket and does populate them.
  const rows = useMemo(
    () =>
      view === 'origination' ? OPPORTUNITY_BUCKETS.filter((b) => !b.parent) : OPPORTUNITY_BUCKETS,
    [view],
  );
  const openMethodology = useOpenMethodology();
  const [sel, setSel] = useState<{ bucket: OpportunityBucket; col: string } | null>(null);
  // The Corryk Opportunity Index is OUR proprietary composite — the factor
  // weights are fixed (DEFAULT_WEIGHTS), not user-tunable. The decomposition is
  // shown read-only so the index is never a black box, but users can't reweight.

  // Country rollup (A) is cross-market — fetch ALL live markets and ignore the
  // per-panel picker; the region heatmap (B) is the one selected market.
  const { data, isLoading, isError } = useMarketOpportunity(
    altitude === 'country' ? undefined : country,
    altitude,
    regionLevel,
  );

  // Origination components (region × canonical-subsegment) — fetched for any
  // region-altitude view so the Corryk Opportunity Index is available both as
  // the heat layer AND in every cell detail. Region-only. (The /origination
  // route name is the backend feed; the user-facing region×subsector metric is
  // the Corryk Opportunity Index. "Corryk Origination Score" is a separate,
  // future entity-level metric — do not reuse that name here.)
  const { data: origData } = useOrigination(country, {
    enabled: altitude === 'region',
    level: regionLevel,
  });

  // Fold canonical-subsegment origination cells up to the bucket × region matrix
  // and blend with the fixed DEFAULT_WEIGHTS. f = Σ(f·c·n)/Σ(c·n); c = Σ(c·n)/Σn.
  const origByCell = useMemo(() => {
    const m = new Map<string, { comp: FactorComp; score: number | null; nTotal: number }>();
    if (altitude !== 'region') return m;
    const acc = new Map<
      string,
      { byF: Record<FactorKey, { fcn: number; cn: number }>; nTotal: number }
    >();
    for (const cell of origData?.cells ?? []) {
      const bucket = CANONICAL_TO_BUCKET[cell.subsegment];
      if (!bucket) continue;
      const key = `${bucket}|${cell.region}`;
      let a = acc.get(key);
      if (!a) {
        a = {
          byF: Object.fromEntries(ORIG_FACTORS.map((f) => [f.key, { fcn: 0, cn: 0 }])) as Record<
            FactorKey,
            { fcn: number; cn: number }
          >,
          nTotal: 0,
        };
        acc.set(key, a);
      }
      a.nTotal += cell.nTotal;
      for (const f of ORIG_FACTORS) {
        const x = (cell as OriginationCell)[f.key];
        if (x.f != null && x.c > 0) {
          a.byF[f.key].fcn += x.f * x.c * cell.nTotal;
          a.byF[f.key].cn += x.c * cell.nTotal;
        }
      }
    }
    for (const [key, a] of acc) {
      const comp = {} as FactorComp;
      for (const f of ORIG_FACTORS) {
        const bf = a.byF[f.key];
        comp[f.key] = bf.cn > 0 ? { f: bf.fcn / bf.cn, c: bf.cn / a.nTotal } : { f: null, c: 0 };
      }
      m.set(key, { comp, score: blendScore(comp, DEFAULT_WEIGHTS), nTotal: a.nTotal });
    }
    return m;
  }, [altitude, origData]);

  // Drill ↓ one altitude. From a REGION cell → pop out a Screener pre-filtered
  // to the cell (market + region + the bucket's canonical sub_segments,
  // independent only). From a COUNTRY cell → drop this panel to the region
  // heatmap for that market (A → B → region cell → Screener). The Screener's
  // default scope already gates to ≥€1M and sorts by turnover desc.
  const apiRef = useDockviewApi();
  const addPanel = useAppStore((s) => s.actions.addPanel);
  function drillToScreener(bucket: OpportunityBucket, region: string, mkt: MarketCode) {
    const api = apiRef.current;
    if (!api) return;
    // The dockview tab stays the canonical "Screener" — a standard Screener,
    // just pre-filtered (country + region as chips, bucket + independent
    // applied server-side). No contextual name.
    const newPanelId = addPanel(tabId, { component: 'screener', title: 'Screener' });
    const cascade = api.panels.filter((p) => p.api.location.type === 'floating').length;
    const offset = 24 * cascade;
    api.addPanel<PanelParams>({
      id: newPanelId,
      component: 'screener' as PanelComponentKey,
      title: 'Screener',
      params: {
        tabId,
        panelId: newPanelId,
        initialState: {
          presetCountry: mkt,
          presetRegion: region,
          // Seeds the Screener's mixed sub-segment chip with the canonical
          // label (parent bucket) or the specialty token (fine bucket). NOT the
          // legacy presetSubSegmentsCanonical, which filtered
          // entities.sub_segment and so opened an empty Screener in the 21
          // markets that column never reached.
          presetSubsegment: subsegmentChipForBucket(bucket),
          presetIndependentOnly: true,
        },
      },
      // Size the pop-out to the Screener's natural column width so it renders
      // the same columns as the docked Screener (it IS the same panel — the
      // widths are fixed/shared; a narrow float just hides columns).
      floating: { position: { left: 100 + offset, top: 80 + offset }, width: 1040, height: 560 },
    });
  }
  function drillToRegion(mkt: MarketCode) {
    setSel(null);
    pickCountry(mkt);
    setAltitude('region');
  }

  const cells = data?.cells ?? [];

  // Matrix model. Column axis:
  //   • country (A) → the full 27-market roster (was the 11-entry TARGET_MARKETS
  //     list, which predates the 27-market decision; the API has rolled up all
  //     27 for a while). Every market is a column; those with no cells render as
  //     the coverage layer. The set is the strategy roster, NOT whatever the API
  //     happened to return — a market missing from the response must read as
  //     "no coverage", not vanish from the comparison.
  //   • region (B) → the live market's regions, ordered by total market € desc.
  const { cols, colLabels, cellMap, ranges } = useMemo(() => {
    const labels = new Map<string, string>();
    const map = new Map<string, MarketOpportunityCell>();
    const byCol = new Map<string, number>();
    let mLo = Infinity;
    let mHi = -Infinity;
    for (const c of cells) {
      const col = altitude === 'country' ? c.country : c.region;
      map.set(`${c.bucket}|${col}`, c);
      byCol.set(col, (byCol.get(col) ?? 0) + c.marketRevenueEur);
      if (c.marketRevenueEur > 0) {
        mLo = Math.min(mLo, Math.log10(c.marketRevenueEur));
        mHi = Math.max(mHi, Math.log10(c.marketRevenueEur));
      }
    }
    let ordered: string[];
    if (altitude === 'country') {
      ordered = [...REGION_MARKETS];
      for (const c of REGION_MARKETS) labels.set(c, MARKET_NAMES[c]);
    } else {
      for (const col of byCol.keys()) labels.set(col, col);
      ordered = [...byCol.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
    }
    return {
      cols: ordered,
      colLabels: labels,
      cellMap: map,
      ranges: { market: { lo: mLo, hi: mHi } } as Ranges,
    };
  }, [cells, altitude]);

  // Fallback guards against a stale view value (e.g. HMR-preserved state for a
  // view that no longer exists) crashing the whole panel.
  const v = VIEWS[view] ?? VIEWS.origination;
  const selCell = sel ? (cellMap.get(`${sel.bucket}|${sel.col}`) ?? null) : null;

  // The panel is freely resizable — it does NOT auto-fit/lock to the matrix
  // size. The grid scrolls within whatever size the user gives the panel
  // (.mo-gridwrap overflow auto), so a small panel scrolls rather than being
  // pinned open. (Was a setConstraints/setSize lock — removed by request.)

  return (
    <PanelFrame tabId={tabId} panelId={panelId}>
      <div className="mo-root">
        <style>{MO_CSS}</style>

        <div className="mo-toolbar">
          <div className="mo-seg" role="group" aria-label="Altitude">
            <button
              type="button"
              className={altitude === 'country' ? 'on' : ''}
              onClick={() => {
                setSel(null);
                setAltitude('country');
                // Corryk Opportunity Index is region-only; fall back to Market
                // Size for the cross-market rollup.
                if (view === 'origination') setView('market');
              }}
              title="Cross-market: subsegment × country"
            >
              Country
            </button>
            <button
              type="button"
              className={altitude === 'region' ? 'on' : ''}
              onClick={() => {
                setSel(null);
                setAltitude('region');
                // Reimbursement is country-only — fall back to the index.
                if (view === 'reimbursement') setView('origination');
              }}
              title="One market: subsegment × region"
            >
              Region
            </button>
          </div>
          <span className="mo-sep" />
          <div className="mo-seg">
            {(Object.keys(VIEWS) as ViewKey[])
              // Reimbursement is a country-altitude-only lens.
              .filter((k) => k !== 'reimbursement' || altitude === 'country')
              .map((k) => (
                <button
                  key={k}
                  type="button"
                  className={k === view ? 'on' : ''}
                  onClick={() => {
                    setSel(null);
                    setView(k);
                    // Origination is region-altitude only; Reimbursement is
                    // country-altitude only — snap the altitude to match.
                    if (k === 'origination') setAltitude('region');
                    if (k === 'reimbursement') setAltitude('country');
                  }}
                >
                  {VIEWS[k].label}
                </button>
              ))}
          </div>
          {altitude === 'region' && (
            <select
              className="mo-ctl"
              aria-label="Market"
              value={country}
              onChange={(e) => pickCountry(e.target.value as MarketCode)}
            >
              {REGION_MARKETS.map((c) => (
                <option key={c} value={c}>
                  {MARKET_NAMES[c]}
                </option>
              ))}
            </select>
          )}
          {altitude === 'region' && (
            <select
              className="mo-ctl"
              aria-label="Region level"
              title="Region grain (Eurostat NUTS hierarchy)"
              value={regionLevel === 'legacy' ? 'nuts2' : regionLevel}
              onChange={(e) => setRegionLevel(e.target.value as 'nuts1' | 'nuts2' | 'nuts3')}
            >
              <option value="nuts1">NUTS 1 — macro-region</option>
              <option value="nuts2">NUTS 2 — region</option>
              <option value="nuts3">NUTS 3 — province</option>
            </select>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="mo-methodology"
            title="How the Corryk Opportunity Index is built"
            onClick={() => openMethodology('index')}
          >
            <BookOpen size={12} /> Methodology
          </button>
        </div>

        {view === 'reimbursement' ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ReimbursementBody />
          </div>
        ) : (
          <>
            <div className="mo-gridwrap">
              {isError ? (
                <div className="mo-empty">Couldn’t load market data.</div>
              ) : isLoading ? (
                <div className="mo-empty">Loading…</div>
              ) : cells.length === 0 ? (
                <PanelEmptyState fill icon={<Compass size={18} />} title="Map the opportunity">
                  {altitude === 'country'
                    ? 'No cross-market scores yet. The Corryk Opportunity Index ranks each region × sub-segment as soon as the market data lands.'
                    : `No scored regions for ${country} yet — the Opportunity Index fills this grid once that market's data is in.`}
                </PanelEmptyState>
              ) : (
                <table
                  className="mo-tbl"
                  style={{
                    // Clamp the fill: never below a legible ~56px/cell (scroll
                    // past it), never above ~92px/cell (cap when few regions).
                    minWidth: 116 + cols.length * 60,
                    maxWidth: 116 + cols.length * 96,
                  }}
                >
                  <colgroup>
                    <col style={{ width: 116 }} />
                    {cols.map((col) => (
                      <col key={col} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="mo-corner">
                        {altitude === 'country'
                          ? 'Subsegment ↓ / Country →'
                          : 'Subsegment ↓ / Region →'}
                      </th>
                      {cols.map((col) => (
                        <th key={col} className="mo-colhead" title={colLabels.get(col)}>
                          {colLabels.get(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((b) => (
                      <tr key={b.key}>
                        <th
                          className={'mo-rowhead' + (b.parent ? ' mo-rowhead-child' : '')}
                          title={
                            b.parent
                              ? `${OPPORTUNITY_BUCKET_LABELS[b.parent]} → ${b.label}. Detected specialty: reads as "at least this many".`
                              : b.label
                          }
                        >
                          {b.label}
                        </th>
                        {cols.map((col) => {
                          const c = cellMap.get(`${b.key}|${col}`);
                          if (!c) return <td key={col} className="mo-cell mo-na" />;
                          let t: number;
                          let valNode: string;
                          if (view === 'origination') {
                            const score = origByCell.get(`${b.key}|${col}`)?.score ?? null;
                            t = score == null ? 0 : score / 100;
                            // No score / degenerate 0 → dash out (never "0"/"n/a").
                            valNode = score ? String(score) : '—';
                          } else {
                            t = v.t(c, ranges);
                            valNode = v.val(c, ccy);
                          }
                          const bg = ramp(t);
                          const fg = textOn(t);
                          const isSel = sel?.bucket === b.key && sel?.col === col;
                          const sub = view === 'origination' ? '' : v.sub(c);
                          const subHtml =
                            (c.reportedShare < 0.5 || c.cr5 == null ? '◔ ' : '') + sub;
                          return (
                            <td
                              key={col}
                              className={'mo-cell' + (isSel ? ' sel' : '')}
                              style={{ background: bg, color: fg }}
                              title={`${b.label} · ${colLabels.get(col)}`}
                              onClick={() => setSel({ bucket: b.key as OpportunityBucket, col })}
                            >
                              <div className="mo-v">{valNode}</div>
                              <div className="mo-sub">{subHtml.trim() || ' '}</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mo-foot">
              <span className="mo-lg">
                {v.scale.lo}
                <span className="mo-ramp" style={{ background: rampGradient() }} />
                {v.scale.hi}
                <span style={{ marginLeft: 4 }}>· {v.scale.cap}</span>
              </span>
              <span className="mo-lg">◔ modelled-heavy (under 50% filed)</span>
              {altitude === 'country' && (
                <span
                  className="mo-lg mo-note"
                  title="A concentration ratio is a fixed top-5 share, so a larger market reads lower for the same industry structure — the national leaders are regional. Compare CR5 country-to-country here, or region-to-region in the Region view, but not across the two."
                >
                  ⓘ CR5 is national-scale — larger markets read lower; compare across countries, not
                  against the Region view
                </span>
              )}
            </div>

            {selCell &&
              sel &&
              createPortal(
                <CellDetail
                  cell={selCell}
                  altitude={altitude}
                  bucketLabel={
                    OPPORTUNITY_BUCKETS.find((b) => b.key === sel.bucket)?.label ?? sel.bucket
                  }
                  colLabel={colLabels.get(sel.col) ?? sel.col}
                  countryCode={altitude === 'country' ? sel.col : country}
                  ccy={ccy}
                  origEntry={origByCell.get(`${sel.bucket}|${sel.col}`) ?? null}
                  canDrill={
                    altitude === 'country' ? REGION_MARKETS.includes(sel.col as MarketCode) : true
                  }
                  onClose={() => setSel(null)}
                  onDrill={() =>
                    altitude === 'country'
                      ? drillToRegion(sel.col as MarketCode)
                      : drillToScreener(sel.bucket, sel.col, country)
                  }
                />,
                document.body,
              )}
          </>
        )}
      </div>
    </PanelFrame>
  );
}

function rampGradient() {
  return `linear-gradient(90deg, ${ramp(0)}, ${ramp(0.5)}, ${ramp(1)})`;
}

// ---- floating cell-detail panel ------------------------------------------

function CellDetail({
  cell,
  altitude,
  bucketLabel,
  colLabel,
  countryCode,
  ccy,
  origEntry,
  canDrill,
  onClose,
  onDrill,
}: {
  cell: MarketOpportunityCell;
  altitude: Altitude;
  bucketLabel: string;
  /** The column's display name — a region name (B) or a country name (A). */
  colLabel: string;
  /** ISO country of this cell (the selected market in B, the cell's own in A). */
  countryCode: string;
  ccy: GlobalCurrency;
  /** Corryk Opportunity Index components for this cell (region-altitude only). */
  origEntry: { comp: FactorComp; score: number | null; nTotal: number } | null;
  /** Whether the A→B region drill is available for this market (live markets). */
  canDrill: boolean;
  onClose: () => void;
  onDrill: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  function onMouseDown(e: React.MouseEvent) {
    if ((e.target as HTMLElement).closest('.mo-winbtn')) return;
    const r = cardRef.current!.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    setPos({ left: r.left, top: r.top });
    const move = (ev: MouseEvent) => {
      if (!drag.current) return;
      setPos({
        left: Math.min(window.innerWidth - 48, Math.max(-392, ev.clientX - drag.current.dx)),
        top: Math.min(window.innerHeight - 26, Math.max(0, ev.clientY - drag.current.dy)),
      });
    };
    const up = () => {
      drag.current = null;
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
  }

  const score = origEntry?.score ?? null;
  const t = score == null ? 0 : score / 100;
  const repPct = Math.round(cell.reportedShare * 100);
  const repChip =
    cell.reportedShare >= 0.66
      ? 'reported-led'
      : cell.reportedShare >= 0.5
        ? 'mixed'
        : '◔ modelled-heavy';

  return (
    <div
      ref={cardRef}
      className="mo-card"
      style={pos ? { left: pos.left, top: pos.top, transform: 'none' } : undefined}
    >
      <style>{MO_CSS}</style>
      <div className="mo-card-chrome" onMouseDown={onMouseDown}>
        <span className="mo-wintitle">
          <span className="mo-grip">⠿</span> Cell detail
        </span>
        <button className="mo-winbtn danger" onClick={onClose} aria-label="Close" type="button">
          ✕
        </button>
      </div>
      <div className="mo-card-body">
        <div className="mo-crumb">{altitude === 'country' ? 'Cross-market' : countryCode}</div>
        <h2 className="mo-h2">{bucketLabel}</h2>
        <div className="mo-where">{colLabel} · private operators, all sizes</div>

        <div className="mo-hero">
          {origEntry && (
            <div className="mo-score" style={{ background: ramp(t), color: textOn(t) }}>
              <div className="mo-score-n">{score ? score : '—'}</div>
              <div className="mo-score-k">index</div>
            </div>
          )}
          <div className="mo-frag">
            <div className="mo-frag-lab">
              {origEntry ? 'Corryk Opportunity Index' : 'Fragmentation'}
            </div>
            <div className="mo-frag-val">{fragLabel(cell.cr5)}</div>
            <div className="mo-frag-note">
              {cell.cr5 == null
                ? `every operator carries the same modelled revenue · ${fmtN(cell.nTotal)} operators`
                : `1 − CR5 = ${(1 - cell.cr5).toFixed(2)} · ${fmtN(cell.nTotal)} operators`}
            </div>
          </div>
        </div>

        {origEntry && (
          <div style={{ marginTop: 12 }}>
            {ORIG_FACTORS.map((f) => {
              const x = origEntry.comp[f.key];
              const has = !!x && x.f != null && x.c > 0;
              const pctv = has ? Math.round(x!.f! * 100) : null;
              return (
                <div key={f.key} className="mo-orow">
                  <span className="mo-olab">{f.label}</span>
                  <span className="mo-obar">
                    <span
                      style={{
                        width: `${has ? pctv : 0}%`,
                        background: has ? ramp(x!.f!) : 'transparent',
                      }}
                    />
                  </span>
                  <span className="mo-oval">{has ? pctv : <span className="mo-oest">—</span>}</span>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 12 }}>
          <Stat k="Operators" sub="all sizes, private" v={fmtN(cell.nTotal)} />
          <Stat
            k="Market revenue"
            sub="Σ all operators, reported + modelled"
            v={(cell.reportedShare < 0.5 ? '◔ ' : '') + fmtMoney(cell.marketRevenueEur, ccy, 2)}
          />
          <Stat
            k="Operators ≥€150k"
            v={
              <>
                <span className="mo-ref">{fmtMoney(cell.addressableRevenueEur, ccy, 2)}</span>
                {fmtN(cell.nSurfaced)}
              </>
            }
            hasChild
          />
          <Stat
            k="↳ of which independent"
            v={
              <>
                <span className="mo-ref">{fmtMoney(cell.independentEur, ccy, 2)}</span>
                {fmtN(cell.independentN)}
              </>
            }
            child
          />
          <Stat
            k="Top-5 share (CR5)"
            sub={fragLabel(cell.cr5).toLowerCase()}
            v={cell.cr5 == null ? '—' : Math.round(cell.cr5 * 100) + '%'}
          />
          <Stat
            k="HHI"
            sub={hhiLabel(cell.hhi)}
            v={cell.hhi == null ? '—' : Math.round(cell.hhi * 10000).toLocaleString()}
          />
        </div>

        <div className="mo-trust">
          <div className="mo-trust-row">
            <span style={{ fontWeight: 500 }}>Reported share</span>
            <span className="mo-bar">
              <span style={{ width: `${repPct}%` }} />
            </span>
            <span style={{ fontWeight: 600, minWidth: 26, textAlign: 'right' }}>{repPct}%</span>
          </div>
          <div style={{ marginTop: 5 }}>
            <span className="mo-chip">{repChip}</span>{' '}
            <span className="mo-chip">register complete</span>
          </div>
          <div className="mo-cap">
            {repPct}% of revenue is filed; the rest modelled (allowed in aggregate, flagged).
          </div>
        </div>

        {altitude === 'country' && !canDrill ? (
          <div className="mo-drill-na">Region heatmap not yet available for {colLabel}</div>
        ) : (
          <button className="mo-drill" type="button" onClick={onDrill}>
            {altitude === 'country' ? (
              <>
                View {colLabel} region heatmap <span className="mo-arr">→</span>
              </>
            ) : (
              <>
                Explore {fmtN(cell.independentN)} addressable entities{' '}
                <span className="mo-arr">→</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({
  k,
  sub,
  v,
  child,
  hasChild,
}: {
  k: string;
  sub?: string;
  v: React.ReactNode;
  child?: boolean;
  hasChild?: boolean;
}) {
  return (
    <div className={'mo-stat' + (child ? ' child' : '') + (hasChild ? ' has-child' : '')}>
      <span className="mo-stat-k">
        {k}
        {sub ? <span className="mo-stat-sub">{sub}</span> : null}
      </span>
      <span className="mo-stat-v">{v}</span>
    </div>
  );
}

// ---- scoped CSS (ported from the approved mockup) ------------------------

const MO_CSS = `
.mo-root { display:flex; flex-direction:column; height:100%; font-size:var(--t-body); font-variant-numeric:tabular-nums; }
.mo-toolbar { display:flex; align-items:center; gap:var(--s-2); padding:var(--s-2) var(--s-3); border-bottom:1px solid var(--hairline); flex-wrap:wrap; }
.mo-seg { display:inline-flex; border:1px solid var(--hairline); border-radius:4px; overflow:hidden; background:var(--menu-surface); }
.mo-seg button { font:inherit; font-size:var(--t-body); font-weight:500; color:var(--text-secondary); background:transparent; border:none; border-right:1px solid var(--hairline); padding:4px 10px; cursor:pointer; white-space:nowrap; }
.mo-seg button:last-child { border-right:none; }
.mo-seg button.on { background:var(--accent); color:var(--bg); font-weight:600; }
.mo-seg button:not(.on):hover { background:var(--hover-subtle); }
.mo-sep { width:1px; align-self:stretch; background:var(--hairline); margin:2px 0; }
.mo-meta { font-size:var(--t-meta); color:var(--text-tertiary); text-transform:uppercase; letter-spacing:.05em; font-weight:600; }
.mo-field { display:inline-flex; align-items:center; gap:6px; font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; }
.mo-ctl { font:inherit; font-size:var(--t-body); color:var(--text-primary); background:var(--menu-surface); border:1px solid var(--hairline); border-radius:3px; padding:3px 6px; cursor:pointer; text-transform:none; letter-spacing:0; }
.mo-methodology { display:inline-flex; align-items:center; gap:4px; font:inherit; font-size:var(--t-body); font-weight:400; color:var(--text-primary); background:var(--bg); border:1px solid var(--hairline); border-radius:2px; padding:2px 8px; cursor:pointer; white-space:nowrap; }
.mo-methodology:hover { background:var(--hover-subtle); }
.mo-gridwrap { flex:1; overflow:auto; padding:var(--s-3); min-height:0; }
.mo-empty { padding:var(--s-4); color:var(--text-tertiary); }
/* Fill the panel width: region columns share the available width (table-layout
   fixed distributes anything not explicitly sized), clamped via the inline
   min/max-width on the table so cells stay legible (scroll past the min) and
   don't get comically wide when few regions are shown (cap at the max). */
.mo-tbl { border-collapse:separate; border-spacing:4px; width:100%; table-layout:fixed; }
.mo-tbl th, .mo-tbl td { padding:0; }
.mo-corner { text-align:left; vertical-align:bottom; font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; padding:0 8px 6px 4px; white-space:normal; line-height:1.2; width:116px; min-width:116px; }
.mo-colhead { vertical-align:bottom; text-align:center; padding:0 3px 5px; font-size:var(--t-meta); font-weight:600; color:var(--text-secondary); text-transform:uppercase; letter-spacing:.02em; line-height:1.2; white-space:normal; overflow-wrap:anywhere; }
.mo-rowhead { text-align:left; vertical-align:middle; font-size:var(--t-body); font-weight:500; color:var(--text-primary); padding:0 12px 0 4px; white-space:normal; line-height:1.2; width:116px; min-width:116px; }
/* Fine specialty rows sit beneath their parent (whose row is the unresolved
   residual). Indent + a rule so the hierarchy reads at a glance. */
.mo-rowhead-child { padding-left:14px; font-weight:400; color:var(--text-secondary); border-left:2px solid var(--border-subtle, rgba(26,20,16,.12)); }
.mo-cell { height:40px; text-align:center; border-radius:3px; cursor:pointer; border:1px solid rgba(26,20,16,.05); outline:2px solid transparent; outline-offset:-1px; }
.mo-cell .mo-v { font-size:12px; font-weight:600; line-height:1; white-space:nowrap; padding:0 5px; }
.mo-cell .mo-sub { font-size:8px; font-weight:500; opacity:.72; line-height:1.1; margin-top:1px; }
.mo-cell:hover { outline-color:var(--text-primary); }
.mo-cell.sel { outline-color:var(--accent); }
.mo-cell.mo-na { background:${NA_HATCH}; cursor:default; }
.mo-foot { border-top:1px solid var(--hairline); padding:8px var(--s-3); font-size:var(--t-meta); color:var(--text-tertiary); line-height:1.4; display:flex; gap:0 16px; flex-wrap:wrap; align-items:baseline; }
.mo-foot b { color:var(--text-secondary); font-weight:600; }
.mo-lg { display:inline-flex; align-items:center; gap:4px; white-space:nowrap; }
.mo-note { font-style:italic; color:var(--text-secondary); cursor:help; white-space:normal; }
.mo-drill-na { margin-top:14px; padding:9px 12px; border:1px dashed var(--hairline); border-radius:4px; font-size:var(--t-meta); color:var(--text-tertiary); text-align:center; font-style:italic; }
.mo-ramp { display:inline-block; height:8px; width:90px; border-radius:2px; border:1px solid rgba(26,20,16,.15); margin:0 4px; }
.mo-card { position:fixed; left:50%; top:84px; transform:translateX(-50%); width:430px; max-width:calc(100vw - 24px); max-height:calc(100vh - 24px); overflow:auto; background:var(--surface); border:1px solid var(--panel-border); border-radius:4px; box-shadow:0 6px 16px rgba(89,64,41,.18),0 14px 40px rgba(26,20,16,.2); z-index:9500; }
.mo-card-chrome { height:26px; background:var(--surface-elevated); border-bottom:1px solid var(--hairline); display:flex; align-items:center; justify-content:space-between; padding:0 4px 0 var(--s-2); font-size:var(--t-meta); letter-spacing:.06em; text-transform:uppercase; color:var(--text-secondary); font-weight:600; cursor:grab; user-select:none; }
.mo-card-chrome:active { cursor:grabbing; }
.mo-wintitle { display:inline-flex; align-items:center; gap:6px; }
.mo-grip { color:var(--text-tertiary); font-size:11px; letter-spacing:-2px; }
.mo-winbtn { width:20px; height:18px; display:inline-flex; align-items:center; justify-content:center; border:1px solid transparent; background:transparent; color:var(--text-tertiary); font-size:12px; cursor:pointer; border-radius:2px; padding:0; }
.mo-winbtn:hover { background:var(--hover-subtle); color:var(--text-primary); border-color:var(--hairline); }
.mo-winbtn.danger:hover { color:var(--alert); border-color:var(--alert); }
.mo-card-body { padding:var(--s-3) var(--s-4) var(--s-4); }
.mo-crumb { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; }
.mo-h2 { margin:3px 0 1px; font-size:var(--t-page-title); font-weight:700; letter-spacing:-0.01em; }
.mo-where { font-size:var(--t-body); color:var(--text-secondary); }
.mo-hero { margin-top:12px; background:var(--menu-surface); border:1px solid var(--hairline); border-radius:5px; padding:var(--s-3); display:flex; align-items:center; gap:14px; }
.mo-score { width:60px; height:60px; border-radius:6px; display:flex; flex-direction:column; align-items:center; justify-content:center; flex-shrink:0; }
.mo-score-n { font-size:26px; font-weight:700; line-height:1; letter-spacing:-0.02em; }
.mo-score-k { font-size:7.5px; text-transform:uppercase; letter-spacing:.06em; opacity:.9; margin-top:2px; }
.mo-frag { flex:1; }
.mo-frag-lab { font-size:var(--t-meta); text-transform:uppercase; letter-spacing:.05em; color:var(--text-tertiary); font-weight:600; }
.mo-frag-val { font-size:var(--t-section); font-weight:600; margin-top:2px; }
.mo-frag-note { font-size:var(--t-meta); color:var(--text-tertiary); margin-top:2px; }
.mo-stat { display:flex; justify-content:space-between; align-items:baseline; padding:8px 0; border-bottom:1px solid var(--hairline); gap:10px; }
.mo-stat.has-child { border-bottom:none; padding-bottom:2px; }
.mo-stat.child { padding-top:2px; }
.mo-stat.child .mo-stat-k { padding-left:12px; color:var(--text-tertiary); font-size:var(--t-meta); }
.mo-stat-k { font-size:var(--t-body); color:var(--text-secondary); }
.mo-stat-sub { display:block; font-size:var(--t-meta); color:var(--text-tertiary); }
.mo-stat-v { font-size:var(--t-section); font-weight:600; text-align:right; white-space:nowrap; }
.mo-ref { font-size:var(--t-meta); font-weight:500; color:var(--text-tertiary); margin-right:6px; }
.mo-trust { margin-top:12px; border:1px solid var(--hairline); border-radius:4px; padding:7px 10px; }
.mo-trust-row { display:flex; align-items:center; gap:8px; font-size:var(--t-meta); color:var(--text-tertiary); }
.mo-bar { flex:1; height:4px; border-radius:3px; background:${NA_HATCH_DARK}; overflow:hidden; }
.mo-bar > span { display:block; height:100%; background:var(--text-tertiary); }
.mo-chip { display:inline-block; font-size:var(--t-meta); padding:1px 6px; border-radius:3px; border:1px solid var(--hairline); font-weight:500; color:var(--text-tertiary); letter-spacing:.02em; }
.mo-cap { font-size:var(--t-meta); color:var(--text-tertiary); margin-top:5px; line-height:1.45; }
.mo-drill { margin-top:14px; width:100%; height:32px; border:1px solid var(--accent); background:var(--accent); color:var(--bg); border-radius:4px; font:inherit; font-size:var(--t-body); font-weight:600; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:6px; }
.mo-drill:hover { background:var(--accent-hover); border-color:var(--accent-hover); }
.mo-arr { font-size:13px; }
.mo-orow { display:grid; grid-template-columns:84px 1fr 26px; align-items:center; gap:7px; padding:3px 0; font-size:var(--t-meta); }
.mo-olab { color:var(--text-secondary); white-space:nowrap; }
.mo-obar { height:8px; background:${NA_HATCH_DARK}; border-radius:2px; overflow:hidden; }
.mo-obar > span { display:block; height:100%; }
.mo-oval { text-align:right; font-family:var(--font-mono); font-weight:600; }
.mo-oest { font-size:8px; color:var(--text-tertiary); text-transform:uppercase; letter-spacing:.04em; }
`;
